import type { KeyboardEvent } from 'react';

export type TableColumnType =
  | 'string'
  | 'integer'
  | 'long'
  | 'object'
  | 'decimal'
  | 'id'
  | 'double'
  | 'date'
  | 'percent'
  | 'group'
  | 'user';

export type TableDataType = string | number | undefined | null;

export type SortDirection = 'asc' | 'desc';

export interface TableColumn {
  type: TableColumnType;
  label: string;
  key: string;
  sortable?: boolean; // defaults to true
  columns?: TableColumn[]; // nested columns for group-type headers
}

export interface ColumnGroup {
  label: string;
  colSpan: number;
}

export type TableRow = TableDataType[];

export interface TableToolbarProps {
  loading: boolean | undefined;
  isSearchEmpty: boolean;
  searchValue: string | undefined;
  searchPlaceholder: string;
  onSearchChange: ((value: string) => void) | undefined;
  onSearchKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSearchSubmit: () => void;
  onExportClick: () => void;
}

export interface TableProps {
  columns: TableColumn[];
  rows: TableRow[];
  totalCount: number;

  // Sort (controlled, BE-driven). 3-state cycle: asc → desc → cleared
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSortChange?: (
    column: string | undefined,
    direction: SortDirection | undefined
  ) => void;

  // Pagination (controlled, BE-driven)
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  // Search (controlled, BE-driven)
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;

  // Button handlers
  onDrilldown?: (id: string | number) => void;
  onExportToCSV: () => Promise<void>;

  // Grouped column headers (optional)
  columnGroups?: ColumnGroup[];

  // Bordered cells — adds 1px #D4D4D4 border to every cell
  borderedCells?: boolean;

  // Loading state
  loading?: boolean;

  // Error state
  error?: Error | null;
}

export const EXPORT_ERROR_MESSAGE = 'Unable to export. Please try again.';
export const DATA_ERROR_MESSAGE =
  'Unable to load. Try refreshing the page or clearing your cookies.';
export const DATA_UNAVAILABLE_MESSAGE = 'Data not yet available.';
